import React, { useEffect, useRef, useState } from 'react'
import { getName } from 'utils'
import { bindActionCreators } from 'redux'
import appBridge from 'libs/appBridge'
import styled from 'styled-components'
import useLoading from 'hooks/useLoading'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import DashboardLayout from 'layouts/DashboardLayout'
import { Grid, Input, Paginator, Breadcrumb } from '@shopline/dashboard-ui'
import { Button, Loading, BasicTable, LayoutDiv, EmptyString, AccordionSelect } from 'components'
import * as productActionCreators from 'modules/products/actionCreator'
import { apiGetCategory } from 'api/products'

import styles from 'scss/Products.module.scss'
import noImage from 'assets/images/no-image.svg'

const StyledSearchSection = styled.section`
  display: flex;
  justify-content: space-between;
`

const StyledSearchInputGroup = styled.div`
  display: flex;
  align-items: flex-start;

  button {
    margin-left: 6px;
  }
`

const StyledImage = styled.div`
  width: 120px;
  height: 120px;
  background-color: #edeef0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const Products = () => {
  const dispatch = useDispatch()
  const { fetchGetProducts, selectProduct } = bindActionCreators(productActionCreators, dispatch)

  const { pagination, productsData } = useSelector((reduxState) => reduxState.products)
  const [isLoading, openLoading, closeLoading] = useLoading()
  const { t, i18n } = useTranslation()
  const [categories, setCategories] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [searchProductName, setSearchProductName] = useState('')
  const [searchCategories, setSearchCategories] = useState([])

  const fetchCategoryData = async () => {
    const categoryResponse = await apiGetCategory()
    const {
      data: { categories },
    } = categoryResponse
    return categories
  }

  const fetchProducts = async (payload = {}) => {
    try {
      openLoading()
      await fetchGetProducts(payload)
      closeLoading()
      return Promise.resolve()
    } catch (errMsg) {
      console.error(errMsg)
      closeLoading()
      return Promise.reject()
    }
  }

  const handleChangePage = (page) => {
    if (page === currentPage) {
      return
    }

    const params = { category_id: searchCategories, keyword: searchProductName, page }

    fetchProducts(params)
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSearchProducts = () => {
    const params = { category_id: searchCategories, keyword: searchProductName, page: 1 }

    fetchProducts(params)
    setCurrentPage(1)
  }

  const handleCategoriesChange = (categories) => {
    const params = { category_id: categories, keyword: searchProductName, page: 1 }

    setSearchCategories(categories)
    fetchProducts(params)
    setCurrentPage(1)
  }

  const handleInitCategory = async () => {
    const categories = await fetchCategoryData()
    setCategories(categories)
  }

  useEffect(() => {
    const initFlow = async () => {
      openLoading()
      await handleInitCategory()
      await fetchProducts()
      closeLoading()
    }

    initFlow()
  }, [])

  return (
    <DashboardLayout>
      <LayoutDiv className="mb-3">
        <Breadcrumb
          items={[
            {
              id: 'apps',
              label: t('apps'),
            },
            {
              id: 'myApps',
              label: t('myApps'),
              url: '/apps/my_apps',
              onClick: async () => {
                appBridge.client.redirect('/apps/my_apps')
              },
            },
            {
              id: 'multiVariation',
              label: t('appName'),
            },
          ]}
        />
      </LayoutDiv>

      <LayoutDiv>
        <h2>{t('appName')}</h2>
      </LayoutDiv>

      {isLoading && <Loading />}

      {productsData ? (
        <div>
          <LayoutDiv>
            <StyledSearchSection>
              <StyledSearchInputGroup>
                <Input
                  style={{ width: '400px' }}
                  value={searchProductName}
                  type="searchProduct"
                  placeholder={t('productSearchPlace')}
                  onChange={(e) => setSearchProductName(e.target.value)}
                  status="valid"
                  block
                />
                <Button color="primary" onClick={handleSearchProducts}>
                  {t('search')}
                </Button>
              </StyledSearchInputGroup>

              <AccordionSelect categories={categories} onCategoryChange={handleCategoriesChange} />
            </StyledSearchSection>
          </LayoutDiv>
          <Grid.Row>
            <Grid.Column lg={12} className={styles['products-table']}>
              <BasicTable
                isLoading={false}
                columns={[
                  { key: 'image', title: t('image'), width: '20%' },
                  { key: 'productName', title: t('productName') },
                  { key: 'variation', title: t('variant') },
                  { key: 'action', title: '' },
                ]}
                dataSrc={productsData.map((product) => ({
                  image: (
                    <StyledImage>
                      <img
                        alt={getName({ obj: product.title_translations, i18n: i18n.language })}
                        src={product.thumbnail}
                        onError={(e) => {
                          e.target.style.width = '50%'
                          e.target.style.height = 'auto'
                          e.target.src = noImage
                        }}
                      />
                    </StyledImage>
                  ),
                  productName: getName({ obj: product.title_translations, i18n: i18n.language }) || <EmptyString />,
                  variation: product.variations
                    .map(({ fields_translations }) => getName({ obj: fields_translations, i18n: i18n.language }))
                    .join(',') || <EmptyString />,
                  action: (
                    <Link
                      className={styles['edit-button']}
                      to={{
                        pathname: `/products/${product._id}/variation`,
                      }}
                    >
                      <Button color="primary" onClick={() => selectProduct({ id: product._id })}>
                        {t('editVariationImages')}
                      </Button>
                    </Link>
                  ),
                }))}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column lg={12} className={styles['page-switch-controller']}>
              <Paginator
                windowSize={5}
                page={currentPage}
                totalCount={pagination.total_pages}
                onPageChange={handleChangePage}
                limit={1}
              />
            </Grid.Column>
          </Grid.Row>
        </div>
      ) : null}
    </DashboardLayout>
  )
}
export default Products
